'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { SquareIcon } from '@terra/ui-icons';
import { VStack } from '@terra/ui-styled-system';
import { IconButton } from '../../../../../components/icon-button';
import { Tooltip } from '../../../../../components/tooltip';
import { Text } from '../../../../../components/styled/text';
import { InlineToolExpand } from '../../InlineToolExpand';
import { ToolResultRenderer } from '../../ToolCallsRenderers/ToolResultRenderer';
import { prepareToolResultData } from '../../ToolCallsRenderers/content-detection';
import { getSubAgentDisplayInput } from './subagent-input-parser';
import type { CardRenderContext } from '../../tool-config-types';
import { hasRenderableNode } from '../../chat-renderability';
import { readVisibleAgentWriteup } from '../trace-investigation/strip-investigation-writeup';
import { headlineKindForParentTool } from '../../chat-headline-actions';

function SubagentStopButton({
  toolCallId,
  onAbortSubagent,
}: {
  toolCallId: string;
  onAbortSubagent: (event: { toolCallId: string }) => void;
}) {
  const tCommon = useTranslations('common');
  const label = tCommon('stop');

  return (
    <Tooltip title={label} positioning={{ placement: 'top' }}>
      <IconButton
        variant="ghost"
        size="xs"
        aria-label={label}
        stopPropagation
        onClick={() => onAbortSubagent({ toolCallId })}
      >
        <SquareIcon size={12} fill="currentColor" strokeWidth={0} />
      </IconButton>
    </Tooltip>
  );
}

export const renderSubAgentCard = (ctx: CardRenderContext): ReactNode => {
  const isRunning =
    ctx.isGroupRunning ||
    ctx.toolCallData.status === 'started' ||
    ctx.toolCallData.status === 'in-progress';
  const expandStatus =
    isRunning && ctx.toolCallData.status !== 'error'
      ? ctx.toolCallData.status === 'started'
        ? 'started'
        : 'in-progress'
      : ctx.toolCallData.status;
  const query = getSubAgentDisplayInput(ctx.toolCallData.input);
  const hasNestedContent = hasRenderableNode(ctx.nestedContent);
  const rawOutput = ctx.toolCallData.output;
  const visibleOutput =
    typeof rawOutput === 'string'
      ? readVisibleAgentWriteup(rawOutput)
      : rawOutput;

  const outputData = visibleOutput
    ? prepareToolResultData({
        content: visibleOutput,
        toolName: ctx.toolName,
      })
    : null;
  const hasDisplayContent =
    Boolean(query) || Boolean(outputData) || hasNestedContent;
  const displayText = isRunning ? ctx.labelPresent : ctx.labelPast;
  const shouldHideEmptyCompletedCard =
    !isRunning && !ctx.toolCallData.error && !hasDisplayContent && !displayText;

  if (shouldHideEmptyCompletedCard) {
    return null;
  }

  const hasToolContent = Boolean(query) || Boolean(outputData);
  const toolCallId = ctx.toolCallData.toolCallId;
  const trailingAction =
    isRunning && toolCallId && ctx.onAbortSubagent ? (
      <SubagentStopButton
        toolCallId={toolCallId}
        onAbortSubagent={ctx.onAbortSubagent}
      />
    ) : undefined;

  const headlineKind = headlineKindForParentTool(ctx.toolName);

  return (
    <InlineToolExpand
      toolName={ctx.toolName}
      displayText={displayText}
      status={expandStatus}
      error={ctx.toolCallData.error}
      isSubAgent
      forceExpanded={ctx.forceExpanded}
      defaultExpanded={ctx.expandWhileRunning === true && isRunning}
      nestedContent={hasNestedContent ? ctx.nestedContent : undefined}
      summary={ctx.nestedSummary}
      liveLabel={ctx.runningText}
      expansionKey={ctx.expansionKey}
      trailingAction={trailingAction}
      rowId={toolCallId ? `chat-subagent-${toolCallId}` : undefined}
      headlineKind={
        headlineKind === 'passthrough' ? undefined : headlineKind
      }
    >
      {hasToolContent ? (
        <VStack alignItems="stretch" gap={0} width="full">
          {query ? (
            <Text fontSize="xs" color="fg.muted" fontStyle="italic">
              {query}
            </Text>
          ) : null}
          {outputData ? (
            <ToolResultRenderer
              data={outputData}
              onOpenObject={ctx.onOpenObject}
            />
          ) : null}
        </VStack>
      ) : null}
    </InlineToolExpand>
  );
};
